import { ViewportScroller } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { EduActivitiesService, EduActivity, MultipleChildResult, PapCategoriesService, Tag } from '@isophi/pap-api';
import { CurrentPapCategoriesService } from '@isophi/pap-api';
import { distinctUntilChanged, filter, map, shareReplay, switchMap, take, takeUntil } from 'rxjs/operators';
import { ChildService } from '../../../../core/services/child.service';
import { HelperService } from '../../../../core/services/helper.service';
import { ResultService } from '../../../../core/services/result.service';
import { combineLatest, Observable } from 'rxjs';
import { BaseComponent } from '../../../../shared/components/base-component/base.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { mapPapCategories } from '@app/modules/recommendations/pages/recommendations/recommendations.component';
import { PapTagCategory } from '@app/modules/recommendations/components/recommendations-cards/recommendations-cards.component';

// Recommendation level = evaluation code of the competence result (0 red L0a, 1 orange L0b, 2 green L1, 3 blue L2–L3).
// Without a result for the competence the teacher gets the L1 variant.
const DEFAULT_RECOMMENDATION_LEVEL = 2;

@Component({
  selector: 'app-recommendation',
  templateUrl: './recommendation.component.html',
  styleUrls: ['./recommendation.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class RecommendationComponent extends BaseComponent implements OnInit, OnDestroy {
  eduActivities$: Observable<EduActivity[]>;

  activeUuid: string | null = null;

  private readonly sanitizer = inject(DomSanitizer);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly viewportScroller = inject(ViewportScroller);
  private readonly childService = inject(ChildService);
  protected readonly helperService = inject(HelperService);
  private readonly translocoService = inject(TranslocoService);
  private readonly papCategoriesService = inject(PapCategoriesService);
  private readonly eduActivitiesService = inject(EduActivitiesService);
  private readonly resultService = inject(ResultService);

  private readonly currentPapCategoriesService = inject(CurrentPapCategoriesService);

  // All PAP categories, old framework included: the detail can still be opened for an old one.
  protected readonly categories$ = this.papCategoriesService.papCategoriesList(undefined, undefined, undefined, 1000).pipe(
    map((categories) => {
      return mapPapCategories(categories);
    }),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  // The cards under the detail offer only the current (MŠMT) framework, like the Doporučení page.
  protected readonly currentCategories$ = this.currentPapCategoriesService
    .currentPapCategoriesList(undefined, undefined, undefined, 1000)
    .pipe(map(mapPapCategories), shareReplay({ refCount: true, bufferSize: 1 }));

  protected readonly papCategory$ = this.activatedRoute.params.pipe(
    filter((params) => !!params.categoryUuid),
    map((params) => params.categoryUuid),
    switchMap((categoryUuid: string) =>
      this.categories$.pipe(
        filter((categories) => !!categories),
        map((categories: PapTagCategory[]) => {
          let found: Tag = null;
          categories.forEach((mainCategory) => {
            if (mainCategory.uuid === categoryUuid) {
              found = mainCategory;
              this.helperService.setTitle(mainCategory.shortName);
              this.helperService.setSubtitle(mainCategory.shortName);
              return;
            }
            mainCategory.categories.forEach((category) => {
              if (category.uuid === categoryUuid) {
                found = category;
                this.helperService.setTitle(category.shortName);
                this.helperService.setSubtitle(mainCategory.shortName);
                return;
              }
            });
          });
          return found;
        })
      )
    ),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

  ngOnInit(): void {
    this.helperService.showBackArrow();
    this.activatedRoute.fragment.subscribe(this.togglePanelFromFragment);
    this.activatedRoute.params.pipe(distinctUntilChanged()).subscribe((params) => {
      this.childService.setSelectedChildByUuid(params.childUuid);
      this.eduActivities$ = this.getRecommendationLevel(params.categoryUuid).pipe(
        switchMap((level) => this.eduActivitiesService.getEduActivities(undefined, [params.categoryUuid], level)),
        takeUntil(this.ngUnsubscribe$),
        map((eduActivities) => eduActivities.results)
      );
    });
  }

  /** Evaluation code of the competence in the selected result, otherwise in the newest result that has it. */
  private getRecommendationLevel(categoryUuid: string): Observable<number> {
    return combineLatest([this.resultService.getSelectedResult(), this.resultService.allTeacherResults$]).pipe(
      take(1),
      map(([selectedResult, results]) => {
        const newestFirst = [...results].sort((a, b) => new Date(b.finishedAt).getTime() - new Date(a.finishedAt).getTime());
        const categoryResult = [selectedResult, ...newestFirst]
          .map((result: MultipleChildResult | null) => result?.categoryResults.find((category) => category.categoryUuid === categoryUuid))
          .find((category) => category?.evaluationCode !== undefined && category?.evaluationCode !== null);
        return categoryResult?.evaluationCode ?? DEFAULT_RECOMMENDATION_LEVEL;
      })
    );
  }

  ngOnDestroy(): void {
    this.helperService.clearSubtitle();
    this.helperService.hideBackArrow();
  }

  sanitize(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  scrollTo(uuid: string) {
    this.activeUuid = uuid;
    setTimeout(() => {
      const element = document.getElementById(uuid);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - 140;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }, 100);
  }

  private togglePanelFromFragment = (categoryFragment: string) => {
    // const categoryUuidFromFragment = categoryFragment?.replace('category-', '') || null;
    // this.pickedCategoryUuid = categoryUuidFromFragment;
    // setTimeout(() => this.viewportScroller.scrollToAnchor(categoryFragment), 0);
  };
}
